<template>
  <div class="h-rep">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-rep__content">
      <!-- ===== Page head + range picker ===== -->
      <div class="h-rep__head">
        <div>
          <h1 class="h-rep__title">Reporting</h1>
          <p class="h-rep__sub">Revenue, retention and licence health{{ currency ? ` · ${currency}` : '' }}.</p>
        </div>
        <div class="h-rep__range">
          <div class="h-seg" role="tablist" aria-label="Date range">
            <button
              v-for="r in ranges"
              :key="r.key"
              type="button"
              class="h-seg__btn"
              :class="{ 'h-seg__btn--on': range === r.key }"
              :aria-pressed="range === r.key"
              @click="setRange(r.key)"
            >{{ r.label }}</button>
          </div>
          <button class="h-btn h-btn--secondary" disabled title="CSV export arrives with the WS4 reporting depth">
            <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            Export
          </button>
        </div>
      </div>

      <!-- ===== Page-level error ===== -->
      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load reporting"
        :message="error"
        @retry="load"
      />

      <!-- ===== Loading skeleton ===== -->
      <template v-else-if="loading">
        <div class="h-rep__kpis">
          <div v-for="n in 4" :key="n" class="h-skel-tile">
            <span class="h-sk" style="width:40%"></span>
            <span class="h-sk h-sk--lg" style="width:60%"></span>
            <span class="h-sk" style="width:50%"></span>
          </div>
        </div>
        <HermesEmptyState variant="loading" title="Crunching the numbers" :skeleton-rows="4" style="margin-top:20px" />
      </template>

      <!-- ===== Body ===== -->
      <template v-else>
        <!-- KPI tiles -->
        <div class="h-rep__kpis">
          <HermesStatTile
            label="MRR"
            :value="mrrValue"
            :currency="currency"
            caption="normalised monthly"
          />
          <HermesStatTile
            label="ARR"
            :value="arrValue"
            :currency="currency"
            caption="run-rate"
          />
          <HermesStatTile
            label="Net churn"
            :value="churnDisplay"
            good-direction="down"
            :caption="churnCaption"
          />
          <HermesStatTile
            label="Add-on attach rate"
            :value="attachDisplay"
            :caption="attachCaption"
          />
        </div>

        <!-- Revenue by product + Gateway split -->
        <div class="h-rep__grid2">
          <!-- Revenue by product (stacked column) -->
          <section class="h-card">
            <header class="h-card__head">
              <h3>Revenue by product</h3>
              <span class="h-card__meta">{{ currency }} · stacked · {{ rangeLabel }}</span>
            </header>
            <div class="h-card__pad">
              <div v-if="!revenueCategories.length" class="h-empty-mini">
                No succeeded payments in this window.
              </div>
              <template v-else>
                <HermesChart :option="revenueOption" :height="252" :aria-label="revenueAria">
                  <template #table>
                    <table>
                      <caption>Revenue by product per {{ revenueData.granularity }} ({{ currency }})</caption>
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th v-for="s in revenueData.series" :key="s.product">{{ s.product }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(c, i) in revenueCategories" :key="c">
                          <th>{{ c }}</th>
                          <td v-for="s in revenueData.series" :key="s.product">{{ s.data[i] }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </HermesChart>
                <div class="h-legend">
                  <div v-for="(s, i) in revenueData.series" :key="s.product" class="h-lg">
                    <span class="h-lg__sw" :style="{ background: seriesColor(s, i) }"></span>
                    {{ s.product }}&nbsp;<b>{{ compactMoney(s.total) }}</b>
                  </div>
                </div>
              </template>
            </div>
          </section>

          <!-- Gateway split donut -->
          <section class="h-card">
            <header class="h-card__head">
              <h3>Gateway split</h3>
              <span class="h-card__meta">by processed volume</span>
            </header>
            <div class="h-card__pad" style="padding-bottom:8px">
              <div v-if="!gatewayRows.length" class="h-empty-mini">
                No succeeded payments in this window.
              </div>
              <div v-else class="h-donut-wrap">
                <HermesChart :option="gatewayOption" :height="170" style="width:170px;flex:none" :aria-label="gatewayAria">
                  <template #table>
                    <table>
                      <caption>Gateway split by processed volume ({{ currency }})</caption>
                      <thead><tr><th>Gateway</th><th>Share</th><th>Volume</th></tr></thead>
                      <tbody>
                        <tr v-for="g in gatewayRows" :key="g.gateway">
                          <th>{{ g.label }}</th><td>{{ g.pct }}%</td><td>{{ g.amount }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </HermesChart>
                <div class="h-legend h-legend--col">
                  <div v-for="(g, i) in gatewayRows" :key="g.gateway" class="h-lg">
                    <span class="h-lg__sw" :style="{ background: vizHex(i) }"></span>
                    {{ g.label }}&nbsp;<b>{{ g.pct }}%</b>
                    <span class="h-lg__meta">· {{ compactMoney(g.amount) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Create Pay kickback line (honest £0 today — no adapter yet) -->
            <div v-if="gatewayData" class="h-kickback">
              <div class="h-kickback__ic">
                <svg viewBox="0 0 24 24"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              </div>
              <div>
                <div class="h-kickback__t">Create Pay kickback earned</div>
                <div class="h-kickback__d">{{ (kickback.rate * 100).toFixed(1) }}% revenue-share on {{ compactMoney(kickback.volume) }} network volume</div>
              </div>
              <div class="h-kickback__v">{{ fullMoney(kickback.earned) }}</div>
            </div>
          </section>
        </div>

        <!-- Attach rate + Licence health -->
        <div class="h-rep__grid2b">
          <!-- Attach rate horizontal bars -->
          <section class="h-card">
            <header class="h-card__head">
              <h3>Add-on attach rate</h3>
              <span class="h-card__meta">share of {{ attachData.active_subscriptions }} active subs</span>
            </header>
            <div v-if="!attachData.addons.length" class="h-empty-mini" style="padding:26px 18px">
              No add-on SKUs configured.
            </div>
            <div v-else class="h-attach">
              <div v-for="a in attachData.addons" :key="a.key" class="h-arow">
                <div class="h-an">{{ a.label }} <div class="h-an__s">{{ a.mode === 'recurring' ? 'recurring' : 'one-off' }}</div></div>
                <div class="h-abar"><i :style="{ width: `${Math.max(a.rate, 0)}%` }"></i></div>
                <div class="h-aval">{{ a.rate }}%</div>
              </div>
            </div>
          </section>

          <!-- Licence estate health table -->
          <section class="h-card">
            <header class="h-card__head">
              <h3>Licence estate health</h3>
              <span v-if="licenceData.products.length" class="h-badge h-badge--success">
                <span class="h-dot" style="background:var(--h-success)"></span>{{ licenceData.totals.health_pct }}% valid
              </span>
            </header>
            <div v-if="!licenceData.products.length" class="h-empty-mini" style="padding:26px 18px">
              No activation tokens issued yet.
            </div>
            <table v-else class="h-htable">
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="r">Valid</th><th class="r">Expiring</th><th class="r">Revoked</th><th class="r">Offline</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in licenceData.products" :key="p.product_id">
                  <td><div class="h-pname"><span class="h-pdot" :style="{ background: vizHex(i) }"></span>{{ p.product }}</div></td>
                  <td class="h-num">{{ p.valid }}</td>
                  <td class="h-num" :style="p.expiring ? 'color:var(--h-warning-text)' : ''">{{ p.expiring }}</td>
                  <td class="h-num" :style="p.revoked ? 'color:var(--h-danger-text)' : ''">{{ p.revoked }}</td>
                  <td class="h-num">{{ p.offline }}</td>
                  <td>
                    <div class="h-health">
                      <div class="h-hbar" role="img" :aria-label="healthAria(p)">
                        <span v-for="seg in healthSegments(p)" :key="seg.key" :style="{ width: `${seg.pct}%`, background: seg.color }"></span>
                      </div>
                      <span class="h-hpct" :style="{ color: p.health_pct >= 90 ? 'var(--h-success-text)' : 'var(--h-warning-text)' }">{{ p.health_pct }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <p class="h-rep__foot">
          MRR/ARR are computed on-read.
          <template v-if="churnProvisional">Churn is provisional until a rollup baseline covers the range start.</template>
          <template v-else>Churn is computed from the daily rollup baseline.</template>
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import { resolveHermesPalette } from "@root/services/hermesEchartsTheme";

// HermesReporting — the Plan 007 Phase 5 reporting-shell island
// (admin-reporting.html). Server-driven: the shell HermesReportingPage mounts it
// by NAME. It fetches its own JSON from the five /api/v1/admin/reports feeds
// (all `?range=`), and renders a range picker, KPI tiles, a revenue-by-product
// stacked column (HermesChart, fixed categorical --viz order + direct-labelled
// totals), a gateway-split donut with the gold kickback line, add-on attach-rate
// bars, and a per-product licence-health table with stacked mini health bars.
//
// Honesty (WS3 shell / WS4 depth): every chart shows an empty state on a thin/
// empty range rather than a fabricated series; churn renders provisional and can
// be "—" with the server's reason. Categorical hues (product/gateway series) come
// from the --viz palette in fixed order; the licence-health distribution is the
// ONLY place the reserved status hues are used (DESIGN.md chart rules).
export default {
  name: 'HermesReporting',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    vendorName: { type: String, default: '' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      range: 'ytd',
      ranges: [
        { key: '30d', label: '30D' },
        { key: '90d', label: '90D' },
        { key: 'ytd', label: 'YTD' },
        { key: '12m', label: '12M' },
      ],
      loading: true,
      error: null,
      revenueData: null,
      gatewayData: null,
      attachData: null,
      licenceData: null,
      kpiData: null,
      crumbs: [{ label: 'Overview' }, { label: 'Reporting' }],
    };
  },
  computed: {
    currency() {
      return this.kpiData?.currency || this.revenueData?.currency || this.gatewayData?.currency || 'GBP';
    },
    rangeLabel() {
      return (this.ranges.find((r) => r.key === this.range) || {}).label || this.range;
    },
    // ── KPI values ──
    mrrValue() { return this.kpiData ? Number(this.kpiData.mrr) : 0; },
    arrValue() { return this.kpiData ? Number(this.kpiData.arr) : 0; },
    churnDisplay() {
      const c = this.kpiData?.churn;
      return c && c.rate !== null && c.rate !== undefined ? `${c.rate}%` : '—';
    },
    churnProvisional() {
      const c = this.kpiData?.churn;
      // Default true only until we know; a rollup-sourced churn is authoritative.
      return c ? c.provisional !== false : true;
    },
    churnCaption() {
      const c = this.kpiData?.churn;
      if (!c) return '';
      if (c.rate === null || c.rate === undefined) {
        return this.churnProvisional ? 'provisional · needs rollup baseline' : 'no baseline yet';
      }
      // Real (rollup-sourced) churn drops the "provisional" hedge.
      if (!this.churnProvisional) {
        return `${c.cancelled_in_range} cancelled · ${c.active_at_start} at start`;
      }
      return `provisional · ${c.cancelled_in_range} cancelled`;
    },
    attachDisplay() {
      return this.attachData ? `${this.attachData.attach_rate}%` : '—';
    },
    attachCaption() {
      if (!this.attachData) return '';
      return `${this.attachData.attached_subscriptions} of ${this.attachData.active_subscriptions} active subs`;
    },
    // ── Revenue chart ──
    revenueCategories() { return this.revenueData?.categories || []; },
    columnTotals() {
      const cats = this.revenueCategories;
      const series = this.revenueData?.series || [];
      return cats.map((_, i) => series.reduce((sum, s) => sum + Number(s.data[i] || 0), 0));
    },
    revenueOption() {
      const series = this.revenueData?.series || [];
      const cats = this.revenueCategories;
      const lastIdx = series.length - 1;
      const ink = this.palette.ink;
      const totals = this.columnTotals;
      return {
        grid: { left: 58, right: 20, top: 30, bottom: 28, containLabel: false },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          valueFormatter: (v) => this.fullMoney(v),
        },
        legend: { show: false },
        xAxis: { type: 'category', data: cats.map((c) => this.shortBucket(c)) },
        yAxis: { type: 'value', axisLabel: { formatter: (v) => this.compactAxis(v) } },
        series: series.map((s, i) => ({
          name: s.product,
          type: 'bar',
          stack: 'revenue',
          barMaxWidth: 46,
          itemStyle: { color: this.seriesColor(s, i) },
          data: s.data.map((v) => Number(v)),
          label: i === lastIdx ? {
            show: true,
            position: 'top',
            formatter: (p) => this.compactMoney(totals[p.dataIndex]),
            color: ink,
            fontWeight: 600,
            fontSize: 11.5,
          } : { show: false },
        })),
      };
    },
    revenueAria() {
      const t = this.revenueData?.total;
      return `Stacked ${this.revenueData?.granularity || 'period'} revenue by product, total ${this.fullMoney(t)} over ${this.revenueCategories.length} periods.`;
    },
    // ── Gateway donut ──
    gatewayRows() { return this.gatewayData?.gateways || []; },
    kickback() { return this.gatewayData?.kickback || { rate: 0, volume: '0.00', earned: '0.00' }; },
    gatewayOption() {
      const rows = this.gatewayRows;
      return {
        tooltip: { trigger: 'item', valueFormatter: (v) => this.fullMoney(v) },
        legend: { show: false },
        title: {
          text: this.compactMoney(this.gatewayData?.total || 0),
          subtext: 'volume',
          left: 'center',
          top: 'center',
          textStyle: { fontSize: 20, fontWeight: 600, color: this.palette.ink },
          subtextStyle: { fontSize: 10, color: this.palette.muted },
          itemGap: 2,
        },
        series: [{
          type: 'pie',
          radius: ['58%', '82%'],
          center: ['50%', '48%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          data: rows.map((g, i) => ({
            name: g.label,
            value: Number(g.amount),
            itemStyle: { color: this.vizHex(i) },
          })),
        }],
      };
    },
    gatewayAria() {
      return `Gateway split by processed volume: ${this.gatewayRows.map((g) => `${g.label} ${g.pct}%`).join(', ')}.`;
    },
    palette() {
      // Re-resolved on each access so the theme flip (light/dark) is picked up.
      return resolveHermesPalette();
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      const q = `?range=${this.range}`;
      try {
        const [rev, gw, at, lic, kpi] = await Promise.all([
          this.api.get(`${this.apiBase}/admin/reports/revenue-by-product${q}`),
          this.api.get(`${this.apiBase}/admin/reports/gateway-split${q}`),
          this.api.get(`${this.apiBase}/admin/reports/attach-rate${q}`),
          this.api.get(`${this.apiBase}/admin/reports/licence-health${q}`),
          this.api.get(`${this.apiBase}/admin/reports/mrr-arr-churn${q}`),
        ]);
        this.revenueData = rev;
        this.gatewayData = gw;
        this.attachData = at;
        this.licenceData = lic;
        this.kpiData = kpi;
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load reporting${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    setRange(r) {
      if (this.range === r) return;
      this.range = r;
      this.load();
    },
    // Fixed categorical colour for a revenue series: real products draw --viz-1..5
    // in order; the linkage-less "Other" bucket always draws --viz-neutral.
    seriesColor(s, i) {
      if (s.product_id === null || s.product_id === undefined) return this.vizNeutralHex();
      // Count only real products up to i so "Other" never consumes a slot.
      const realIndex = (this.revenueData?.series || [])
        .slice(0, i)
        .filter((x) => x.product_id !== null && x.product_id !== undefined).length;
      return this.vizHex(realIndex);
    },
    vizHex(i) {
      const p = this.palette.palette;
      return p[i % p.length];
    },
    vizNeutralHex() {
      const p = this.palette.palette;
      return p[p.length - 1]; // --viz-neutral is the last entry
    },
    healthSegments(p) {
      const total = p.total || 1;
      return [
        { key: 'valid', pct: (p.valid / total) * 100, color: 'var(--h-success)' },
        { key: 'expiring', pct: (p.expiring / total) * 100, color: 'var(--h-warning)' },
        { key: 'revoked', pct: (p.revoked / total) * 100, color: 'var(--h-danger)' },
        { key: 'offline', pct: (p.offline / total) * 100, color: 'var(--h-neutral-text)' },
      ].filter((s) => s.pct > 0);
    },
    healthAria(p) {
      return `${p.product}: ${p.valid} valid, ${p.expiring} expiring, ${p.revoked} revoked, ${p.offline} offline of ${p.total}.`;
    },
    shortBucket(c) {
      // '2026-05' → 'May'; '2026-W23' → 'W23'; else verbatim.
      const m = /^(\d{4})-(\d{2})$/.exec(c);
      if (m) {
        const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return names[Number(m[2]) - 1] || c;
      }
      const w = /-W?(\d{1,2})$/.exec(c);
      return w ? `W${w[1]}` : c;
    },
    compactAxis(v) {
      const n = Number(v);
      const sym = this.symbol();
      if (Math.abs(n) >= 1e6) return `${sym}${(n / 1e6).toFixed(1)}M`;
      if (Math.abs(n) >= 1e3) return `${sym}${(n / 1e3).toFixed(1)}K`;
      return `${sym}${n}`;
    },
    compactMoney(v) {
      const n = Number(v);
      const sym = this.symbol();
      if (Number.isNaN(n)) return '—';
      const abs = Math.abs(n);
      const sign = n < 0 ? '-' : '';
      if (abs < 1000) return `${sign}${sym}${abs.toFixed(0)}`;
      let out;
      if (abs < 1e6) out = `${(abs / 1e3).toFixed(1)}K`;
      else out = `${(abs / 1e6).toFixed(1)}M`;
      out = out.replace(/\.0([KM])$/, '$1');
      return `${sign}${sym}${out}`;
    },
    fullMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${this.symbol()}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    symbol() {
      return { GBP: '£', USD: '$', EUR: '€' }[this.currency] || '';
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-rep { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-rep__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-rep__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-rep__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }

.h-rep__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; flex-wrap: wrap; }
.h-rep__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-rep__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-rep__range { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

/* Segmented range control (pill) */
.h-seg { display: inline-flex; background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-pill, 999px); padding: 3px; }
.h-seg__btn { font: inherit; font-size: 12.5px; font-weight: 500; border: none; background: transparent; color: var(--h-text-secondary, #64748B); padding: 6px 14px; border-radius: 999px; cursor: pointer; }
.h-seg__btn--on { background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); box-shadow: var(--h-shadow-card); }
.h-seg__btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; line-height: 1; white-space: nowrap; color: var(--h-text-primary); }
.h-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.h-btn__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary); border-color: var(--h-border-strong, #CBD5E1); }

/* KPI grid */
.h-rep__kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
@media (max-width: 1080px) { .h-rep__kpis { grid-template-columns: repeat(2, 1fr); } }

/* Grids */
.h-rep__grid2 { display: grid; grid-template-columns: 1.7fr 1fr; gap: 20px; margin-top: 20px; align-items: start; }
.h-rep__grid2b { display: grid; grid-template-columns: 1fr 1.2fr; gap: 20px; margin-top: 20px; align-items: start; }
@media (max-width: 1080px) { .h-rep__grid2, .h-rep__grid2b { grid-template-columns: 1fr; } }

/* Card */
.h-card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-card__head { display: flex; align-items: center; justify-content: space-between; padding: 15px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-card__head h3 { margin: 0; font-size: 15px; font-weight: 600; }
.h-card__meta { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); }
.h-card__pad { padding: 18px; }
.h-empty-mini { font-size: 13px; color: var(--h-text-tertiary, #94A3B8); text-align: center; padding: 40px 10px; }

/* Legend */
.h-legend { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 14px; }
.h-legend--col { flex-direction: column; gap: 10px; margin-top: 0; }
.h-lg { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--h-text-secondary, #64748B); }
.h-lg__sw { width: 11px; height: 11px; border-radius: 3px; }
.h-lg b { color: var(--h-text-primary, #0F172A); font-variant-numeric: tabular-nums; }
.h-lg__meta { color: var(--h-text-tertiary, #94A3B8); }

/* Donut */
.h-donut-wrap { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }

/* Kickback line (gold) */
.h-kickback { margin: 6px 18px 18px; padding: 12px 14px; border: 1px solid var(--h-apexl-gold, #C4A882); background: var(--h-apexl-gold-light, #F6F1E7); border-radius: var(--h-radius, 6px); display: flex; align-items: center; gap: 12px; }
.h-kickback__ic { width: 30px; height: 30px; border-radius: 8px; background: var(--h-apexl-gold, #C4A882); color: #3A2E17; display: flex; align-items: center; justify-content: center; flex: none; }
.h-kickback__ic svg { width: 17px; height: 17px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-kickback__t { font-size: 12.5px; font-weight: 600; color: var(--h-apexl-gold-dark, #8B7355); }
.h-kickback__d { font-size: 11.5px; color: var(--h-apexl-gold-dark, #8B7355); opacity: 0.9; }
.h-kickback__v { margin-left: auto; font-family: var(--h-font-mono, monospace); font-weight: 700; font-size: 15px; color: var(--h-apexl-gold-dark, #8B7355); }

/* Attach rate bars */
.h-attach { padding: 16px 18px; }
.h-arow { display: grid; grid-template-columns: 160px 1fr 56px; gap: 12px; align-items: center; padding: 9px 0; }
.h-an { font-size: 13px; }
.h-an__s { font-size: 11.5px; color: var(--h-text-tertiary, #94A3B8); }
.h-abar { height: 10px; background: var(--h-neutral-bg, #F1F5F9); border-radius: 999px; overflow: hidden; }
.h-abar i { display: block; height: 100%; border-radius: 999px; background: var(--viz-1, #0EA5E9); }
.h-aval { font-family: var(--h-font-mono, monospace); font-variant-numeric: tabular-nums; font-size: 12.5px; text-align: right; color: var(--h-text-secondary, #64748B); }

/* Licence health table */
.h-htable { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.h-htable thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); padding: 10px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-htable thead th.r { text-align: right; }
.h-htable tbody td { padding: 12px 18px; border-bottom: 1px solid var(--h-border, #E2E8F0); vertical-align: middle; }
.h-htable tbody tr:last-child td { border-bottom: none; }
.h-htable tbody tr:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-num { font-variant-numeric: tabular-nums; font-family: var(--h-font-mono, monospace); font-size: 13px; text-align: right; }
.h-pname { font-weight: 600; display: flex; align-items: center; gap: 9px; }
.h-pdot { width: 9px; height: 9px; border-radius: 3px; flex: none; }
.h-health { display: flex; align-items: center; gap: 9px; }
.h-hbar { display: flex; height: 9px; border-radius: 3px; overflow: hidden; width: 130px; background: var(--h-neutral-bg, #F1F5F9); }
.h-hbar span { display: block; height: 100%; }
.h-hpct { font-family: var(--h-font-mono, monospace); font-variant-numeric: tabular-nums; font-size: 12.5px; font-weight: 600; }

/* Badges */
.h-badge { display: inline-flex; align-items: center; gap: 6px; padding: 3px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; border: 1px solid transparent; white-space: nowrap; }
.h-badge--success { background: var(--h-success-bg); color: var(--h-success-text); border-color: var(--h-success-border); }
.h-dot { width: 6px; height: 6px; border-radius: 50%; }

.h-rep__foot { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 18px 2px 0; }

/* Skeletons */
.h-skel-tile { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); padding: 18px 20px; box-shadow: var(--h-shadow-card); display: flex; flex-direction: column; gap: 12px; }
.h-sk { height: 12px; border-radius: 5px; background: linear-gradient(90deg, var(--h-bg-subtle, #F1F5F9) 25%, var(--h-border, #E2E8F0) 37%, var(--h-bg-subtle, #F1F5F9) 63%); background-size: 400% 100%; animation: h-sh 1.4s ease infinite; }
.h-sk--lg { height: 26px; }
@keyframes h-sh { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
</style>
